import posthog from "posthog-js";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { formatToHumanReadable } from "@/lib/format-data";
import {
  trackBackwardStep,
  trackLocationSelection,
  trackLocationTypeSelection,
  trackPatientTypeSelection,
  trackProviderSelection,
  trackSelfScheduleStep,
  trackServiceSelection,
  trackTimeSelection,
} from "@/lib/gtm-events";
import { readRwgAttribution, writeRwgAttribution } from "@/lib/rwg-attribution";
import type { BookingPreferencesDraft } from "@/lib/self-schedule-booking-prefill";
import { addonEntryKeys } from "@/lib/service-addon-utils";

import { PatientStatus } from "@/types/enums/user-type";
import type { AvailabilityAssignment } from "@/types/self-schedule";

import {
  createTenantChangeListener,
  createTenantStorage,
} from "./tenant-storage-helpers";

export enum SelfScheduleStep {
  PATIENT_TYPE = "patientType",
  LOCATION_TYPE = "locationType",
  SERVICE = "service",
  LOCATION = "location",
  APPOINTMENT_REVIEW = "appointmentReview",
  TIME = "time",
  CONFIRMATION = "confirmation",
  PAYMENT_METHOD = "paymentMethod",
  SECURE_APPOINTMENT = "secureAppointment",
  CHECKOUT = "checkout",
  BOOKING_COMPLETE = "bookingComplete",
  WAITLIST_COMPLETE = "waitlistComplete",
}

export const isSelfScheduleComplete = (step: SelfScheduleStep | null) =>
  step === SelfScheduleStep.BOOKING_COMPLETE ||
  step === SelfScheduleStep.WAITLIST_COMPLETE;

export enum LocationType {
  VIRTUAL = "virtual",
  IN_PERSON = "in-person",
  ALL = "all",
}

// Sentinel id stored in `selectedProviderId` when the patient opts into
// "first available". The backend treats `providerId: null` as auto-assign;
// using a sentinel lets the UI mark the appointment review step as complete while
// `selectedProvider` (resolved via lookup) remains undefined.
export const FIRST_AVAILABLE_PROVIDER_ID = "__first_available__";

export type PatientType = (typeof PatientType)[keyof typeof PatientType];

export const PatientType = {
  [PatientStatus.NEW]: PatientStatus.NEW,
  [PatientStatus.EXISTING]: PatientStatus.EXISTING,
  [PatientStatus.RETURNING]: PatientStatus.RETURNING,
  ALL: "ALL",
} as const;

// One entry per service the patient has added to the cart. The same service
// id can appear multiple times (e.g. "2 × Small area" in a laser package);
// instanceId distinguishes them so removals can target a single entry.
export interface SelectedServiceInstance {
  id: string;
  instanceId: string;
  name?: string;
}

export function generateInstanceId(): string {
  if (
    typeof globalThis !== "undefined" &&
    typeof (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
      ?.randomUUID === "function"
  ) {
    return (
      globalThis as { crypto: { randomUUID: () => string } }
    ).crypto.randomUUID();
  }
  return `inst_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

interface SelfScheduleFunctions {
  applyBookingPreferences: (draft: BookingPreferencesDraft) => void;
  setUrlPrefilledServices: (services: { id: string; name: string }[]) => void;
  clearUrlPrefilledServices: () => void;
  // Service selection — each call appends a fresh instance, so the same
  // service can be added multiple times.
  addSelectedService: (serviceId: string, serviceName?: string) => string;
  // Remove a single instance by its instanceId.
  removeSelectedService: (instanceId: string) => void;
  // Remove every instance of a given service id (used by the selections
  // drawer "Remove Service" button).
  removeAllInstancesOfService: (serviceId: string) => void;
  clearSelectedServices: () => void;
  setServiceSearchQuery: (query: string) => void;
  setConfirmedServiceSelections: (confirmed: boolean) => void;
  setConfirmedAppointmentReview: (confirmed: boolean) => void;

  // Add-on selections — keyed by service occurrence (`serviceId:index`) for
  // duplicate parent services, with legacy service-id keys still supported.
  setAddonsForService: (selectionKey: string, addonIds: string[]) => void;
  clearAddonsForService: (selectionKey: string) => void;

  // Category filtering
  setSelectedCategory: (category: string) => void;
  clearCategoryFilter: () => void;

  // Provider selection
  setSelectedProviderId: (
    providerId: string | null,
    providerName?: string,
  ) => void;
  selectAvailabilitySlot: (
    dateTime: Date,
    assignments: AvailabilityAssignment[],
    locationId?: string | null,
    end?: Date,
  ) => void;

  // Multi-provider self-scheduling: assign a provider to a single service
  // instance (keyed by SelectedServiceInstance.instanceId). The appointment review step
  // writes the chosen provider onto every instance in a provider group.
  setServiceInstanceProvider: (instanceId: string, providerId: string) => void;
  setServiceInstancesProvider: (
    instanceIds: string[],
    providerId: string,
  ) => void;
  clearServiceInstanceProviders: () => void;
  clearReviewProviderPreferences: () => void;

  // Location
  setSelectedLocationId: (
    locationId: string | null,
    locationName?: string,
  ) => void;
  setUserLocation: (
    location: { lat: number; lng: number; accuracy?: number } | null,
  ) => void;
  requestUserLocation: () => Promise<void>;

  // Time selection
  setSelectedDateTime: (dateTime: Date | null) => void;
  clearSelectedDateTime: () => void;

  // Payment timer
  setPaymentDeadline: (deadline: Date | null) => void;
  getPaymentTimeRemaining: () => number;

  // Utility methods
  resetAll: () => void;

  // check if we need these
  setConfirmed: (confirmed: boolean) => void;
  setCreatedAppointmentId: (appointmentId: string | null) => void;
  setGroupingMode: (mode: "auto" | "location" | "category" | "both") => void;

  setPaymentSuccessful: (paymentSuccessful: boolean) => void;
  setLocationType: (locationType: LocationType | null) => void;
  setPatientType: (patientType: PatientType | null) => void;

  setCurrentStep: (
    step: SelfScheduleStep | null,
    direction: "forward" | "backward",
  ) => void;

  setCancelEventId: (id: string | null) => void;
  setPatientNote: (note: string) => void;
  setDismissedExistingAppointment: (
    dismissedExistingAppointment: boolean,
  ) => void;
  setDismissedCarePlanSuggestion: (
    dismissedCarePlanSuggestion: boolean,
  ) => void;
  setAppliedCarePlanStepId: (stepId: string | null) => void;
  setBookingEventId: (bookingEventId: string | null) => void;
  setPaymentMethodCollected: (collected: boolean) => void;

  // Google Reserve with Google (RWG) tracking
  setRwgToken: (token: string | null) => void;
  setRwgMerchantId: (merchantId: string | null) => void;
}

export interface SelfScheduleFields {
  /** Identity owning the persisted booking draft. */
  bookingScope: string | null;
  // Current step
  currentStep: SelfScheduleStep | null;

  // Service selection
  selectedServiceInstances: SelectedServiceInstance[];
  urlPrefilledServices: { id: string; name: string }[];
  confirmedServiceSelections: boolean;
  confirmedAppointmentReview: boolean;

  // Add-on selections: occurrence key (`serviceId:index`) -> list of service_addon IDs
  selectedAddons: Record<string, string[]>;

  // Category filtering
  selectedCategory: string;

  // Provider selection
  selectedProviderId: string | null;
  // Concrete providers materialized for the selected availability slot, in
  // the same order as that slot's service occurrences.
  selectedAvailabilityAssignments: AvailabilityAssignment[];
  // The selected slot's window as availability returned it (ISO strings). Its
  // end already reflects each assigned provider's own service durations.
  selectedAvailabilityWindow: { start: string; end: string } | null;

  // Provider chosen per service instance when multi-provider self-scheduling is
  // active (keyed by SelectedServiceInstance.instanceId). Empty in the normal
  // single-provider flow, which uses selectedProviderId instead.
  serviceInstanceProviderIds: Record<string, string>;
  serviceInstanceProviderOrigins: Record<string, "imported" | "review">;
  providerPreferenceMode: "grouped" | "per-occurrence";
  addonProviderIds: Record<string, string>;
  preferredProviderEntryKeys: Record<string, string>;

  // Location selection
  selectedLocationId: string | null;

  // User location
  userLocation: { lat: number; lng: number; accuracy?: number } | null;

  // Time selection
  selectedDateTime: Date | null;
  // The location the selected time was found at. A booking of virtual services
  // picks no location, so it is booked there. Read it only with the time it came
  // with: it isn't cleared when the time is.
  selectedSlotLocationId: string | null;
  // Transient action signal: reselecting the same slot must still progress.
  availabilitySelectionRevision: number;

  // Payment timer
  paymentDeadline: Date | null;

  // Utility methods
  lastUpdated: number | null;
  confirmed: boolean;
  createdAppointmentId: string | null;
  paymentSuccessful: boolean;
  locationType: LocationType | null;
  patientType: PatientType | null;

  // Services search/filter state
  serviceSearchQuery: string;

  // Grouping preferences
  groupingMode: "auto" | "location" | "category" | "both";

  // Existing appointment (for rescheduling)
  cancelEventId: string | null;
  dismissedExistingAppointment: boolean;

  // Care plan suggestion dismissal (once dismissed in a session, hide the card)
  dismissedCarePlanSuggestion: boolean;

  // Care plan step id the patient is booking against — linked on the server
  // after the event is created so the step transitions to SCHEDULED.
  appliedCarePlanStepId: string | null;

  // Booking-event link the patient arrived on. Pins the window, services and
  // provider for the whole flow and is re-validated server-side on booking.
  bookingEventId: string | null;

  // Payment method collection
  paymentMethodCollected: boolean;

  // The patient's note for their provider. Never persisted: it can hold health details.
  patientNote: string;

  // Google Reserve with Google (RWG) tracking
  rwgToken: string | null;
  rwgMerchantId: string | null;
}

export interface SelfScheduleStore
  extends SelfScheduleFields, SelfScheduleFunctions {}

const INITIAL_STATE: SelfScheduleFields = {
  bookingScope: null,
  currentStep: null,
  selectedServiceInstances: [],
  urlPrefilledServices: [],
  confirmedServiceSelections: false,
  confirmedAppointmentReview: false,
  selectedAddons: {},
  selectedCategory: "",
  selectedProviderId: null,
  selectedAvailabilityAssignments: [],
  selectedAvailabilityWindow: null,
  serviceInstanceProviderIds: {},
  serviceInstanceProviderOrigins: {},
  providerPreferenceMode: "grouped",
  addonProviderIds: {},
  preferredProviderEntryKeys: {},
  selectedLocationId: null,
  userLocation: null,
  selectedDateTime: null,
  selectedSlotLocationId: null,
  availabilitySelectionRevision: 0,
  lastUpdated: null,
  confirmed: false,
  createdAppointmentId: null,
  paymentSuccessful: false,
  paymentDeadline: null,
  locationType: null,
  patientType: null,
  serviceSearchQuery: "",
  groupingMode: "auto" as const,
  cancelEventId: null,
  dismissedExistingAppointment: false,
  dismissedCarePlanSuggestion: false,
  appliedCarePlanStepId: null,
  bookingEventId: null,
  paymentMethodCollected: false,
  patientNote: "",
  rwgToken: null,
  rwgMerchantId: null,
};

function pruneEntryPreferences(
  state: SelfScheduleFields,
  instances: SelectedServiceInstance[],
  addons: Record<string, string[]>,
) {
  const keys = new Set<string>();
  const counts = new Map<string, number>();
  for (const instance of instances) {
    keys.add(instance.instanceId);
    const count = (counts.get(instance.id) ?? 0) + 1;
    counts.set(instance.id, count);
    const ids =
      addons[`${instance.id}:${count}`] ??
      (count === 1 ? (addons[instance.id] ?? []) : []);
    addonEntryKeys(instance.instanceId, ids).forEach((key) => keys.add(key));
  }
  return {
    serviceInstanceProviderOrigins: Object.fromEntries(
      Object.entries(state.serviceInstanceProviderOrigins).filter(([key]) =>
        keys.has(key),
      ),
    ),
    addonProviderIds: Object.fromEntries(
      Object.entries(state.addonProviderIds).filter(([key]) => keys.has(key)),
    ),
    preferredProviderEntryKeys: Object.fromEntries(
      Object.entries(state.preferredProviderEntryKeys).filter(
        ([source, target]) => keys.has(source) && keys.has(target),
      ),
    ),
  };
}

// Flag to prevent tracking during rehydration
let isRehydrating = false;

export const useSelfScheduleStore = create<SelfScheduleStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setUrlPrefilledServices: (services) =>
        set({ urlPrefilledServices: services }),
      clearUrlPrefilledServices: () => set({ urlPrefilledServices: [] }),
      applyBookingPreferences: (draft) =>
        set({
          urlPrefilledServices: draft.selectedServiceInstances.map(
            (instance) => ({
              id: instance.id,
              name: instance.name ?? instance.id,
            }),
          ),
          selectedLocationId: draft.selectedLocationId,
          selectedServiceInstances: draft.selectedServiceInstances,
          selectedAddons: draft.selectedAddons,
          serviceInstanceProviderIds: draft.serviceInstanceProviderIds,
          serviceInstanceProviderOrigins: Object.fromEntries(
            Object.keys(draft.serviceInstanceProviderIds).map((key) => [
              key,
              "imported" as const,
            ]),
          ),
          addonProviderIds: draft.addonProviderIds,
          preferredProviderEntryKeys: draft.preferredProviderEntryKeys,
          providerPreferenceMode: "per-occurrence",
          selectedProviderId: null,
          confirmed: false,
          confirmedServiceSelections: false,
          confirmedAppointmentReview: false,
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
        }),
      // Step management
      setCurrentStep: (step, direction) => {
        if (step === null) {
          set({ currentStep: null });
          return;
        }
        if (isRehydrating || step === get().currentStep) return;

        if (direction === "backward") {
          trackBackwardStep(get().currentStep, step);
        } else {
          trackSelfScheduleStep(step);
        }
        posthog.capture("self_schedule_step_changed", {
          step,
          previous_step: get().currentStep,
          direction,
        });
        set({ currentStep: step });
      },

      setServiceSearchQuery: (query: string) =>
        set({ serviceSearchQuery: query }),

      setDismissedExistingAppointment: (
        dismissedExistingAppointment: boolean,
      ) => set({ dismissedExistingAppointment }),

      setDismissedCarePlanSuggestion: (dismissedCarePlanSuggestion: boolean) =>
        set({ dismissedCarePlanSuggestion }),

      setAppliedCarePlanStepId: (stepId: string | null) =>
        set({ appliedCarePlanStepId: stepId }),

      setBookingEventId: (bookingEventId: string | null) =>
        set({ bookingEventId }),

      setPatientType: (patientType: PatientType | null) => {
        const prevValue = get().patientType;
        // Only track if value is actually changing and not during rehydration
        if (
          !isRehydrating &&
          patientType !== null &&
          patientType !== prevValue
        ) {
          posthog.capture("self_schedule_patient_type_selected", {
            patient_type: formatToHumanReadable(patientType),
          });
          trackPatientTypeSelection(patientType);
        }
        set({ patientType });
      },

      setCreatedAppointmentId: (appointmentId) =>
        set({ createdAppointmentId: appointmentId }),

      setConfirmedServiceSelections: (confirmed: boolean) =>
        set({ confirmedServiceSelections: confirmed }),

      setConfirmedAppointmentReview: (confirmed: boolean) =>
        set({ confirmedAppointmentReview: confirmed }),

      setConfirmed: (confirmed: boolean) => set({ confirmed }),

      setLocationType: (locationType) => {
        const prevValue = get().locationType;
        // Only track if value is actually changing and not during rehydration
        if (
          !isRehydrating &&
          locationType !== null &&
          locationType !== prevValue
        ) {
          posthog.capture("self_schedule_location_type_selected", {
            location_type: locationType,
          });
          trackLocationTypeSelection(locationType);
        }
        const locationTypeChanged = locationType !== prevValue;
        set({
          locationType,
          confirmed: locationTypeChanged ? false : get().confirmed,
          confirmedAppointmentReview: locationTypeChanged
            ? false
            : get().confirmedAppointmentReview,
        });
      },

      addSelectedService: (serviceId, serviceName) => {
        const { selectedServiceInstances, patientType } = get();
        posthog.capture("self_schedule_service_selected", {
          service_id: serviceId,
          service_name: serviceName,
          // 1-based index of this addition among same-service instances.
          instance_index:
            selectedServiceInstances.filter((s) => s.id === serviceId).length +
            1,
        });
        trackServiceSelection(serviceId, serviceName, patientType);
        const instanceId = generateInstanceId();
        set({
          selectedServiceInstances: [
            ...selectedServiceInstances,
            { id: serviceId, instanceId, name: serviceName },
          ],
          confirmed: false,
          // The availability grid interval is derived from the combined
          // duration of all selected services, so changing the selection can
          // change which slot start times are valid. Drop any previously
          // picked time so a slot chosen under a different service set can't be
          // submitted against a grid that no longer offers it.
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
          confirmedAppointmentReview: false,
        });
        return instanceId;
      },
      removeSelectedService: (instanceId) => {
        const {
          selectedServiceInstances,
          selectedAddons,
          serviceInstanceProviderIds,
        } = get();
        const remaining = selectedServiceInstances.filter(
          (s) => s.instanceId !== instanceId,
        );
        const nextInstanceProviders = { ...serviceInstanceProviderIds };
        delete nextInstanceProviders[instanceId];
        const removedInstance = selectedServiceInstances.find(
          (s) => s.instanceId === instanceId,
        );
        const nextAddons = { ...selectedAddons };
        if (removedInstance) {
          const originalSameService = selectedServiceInstances.filter(
            (s) => s.id === removedInstance.id,
          );
          const remainingSameService = remaining.filter(
            (s) => s.id === removedInstance.id,
          );
          originalSameService.forEach((_, index) => {
            delete nextAddons[`${removedInstance.id}:${index + 1}`];
          });
          remainingSameService.forEach((inst, newIndex) => {
            const oldIndex =
              originalSameService.findIndex(
                (original) => original.instanceId === inst.instanceId,
              ) + 1;
            const addonIds =
              selectedAddons[`${removedInstance.id}:${oldIndex}`];
            if (addonIds) {
              nextAddons[`${removedInstance.id}:${newIndex + 1}`] = addonIds;
            }
          });
          if (remainingSameService.length === 0) {
            delete nextAddons[removedInstance.id];
          }
        }
        set({
          ...pruneEntryPreferences(get(), remaining, nextAddons),
          selectedServiceInstances: remaining,
          selectedAddons: nextAddons,
          serviceInstanceProviderIds: nextInstanceProviders,
          confirmed: false,
          confirmedAppointmentReview: false,
          // Combined duration changed -> slot grid may change; invalidate time.
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
        });
      },
      removeAllInstancesOfService: (serviceId) => {
        const {
          selectedServiceInstances,
          selectedAddons,
          serviceInstanceProviderIds,
        } = get();
        const nextAddons = { ...selectedAddons };
        delete nextAddons[serviceId];
        const nextInstanceProviders = { ...serviceInstanceProviderIds };
        selectedServiceInstances
          .filter((s) => s.id === serviceId)
          .forEach((instance, index) => {
            delete nextAddons[`${serviceId}:${index + 1}`];
            delete nextInstanceProviders[instance.instanceId];
          });
        set({
          ...pruneEntryPreferences(
            get(),
            selectedServiceInstances.filter((s) => s.id !== serviceId),
            nextAddons,
          ),
          selectedServiceInstances: selectedServiceInstances.filter(
            (s) => s.id !== serviceId,
          ),
          selectedAddons: nextAddons,
          serviceInstanceProviderIds: nextInstanceProviders,
          confirmed: false,
          confirmedAppointmentReview: false,
          // Combined duration changed -> slot grid may change; invalidate time.
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
        });
      },
      clearSelectedServices: () =>
        set({
          selectedServiceInstances: [],
          serviceInstanceProviderOrigins: {},
          addonProviderIds: {},
          preferredProviderEntryKeys: {},
          selectedAddons: {},
          serviceInstanceProviderIds: {},
          confirmed: false,
          confirmedAppointmentReview: false,
          // Combined duration changed -> slot grid may change; invalidate time.
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
        }),

      // Add-on selections
      setAddonsForService: (serviceId, addonIds) => {
        const { selectedAddons } = get();
        set({
          ...pruneEntryPreferences(get(), get().selectedServiceInstances, {
            ...selectedAddons,
            [serviceId]: addonIds,
          }),
          selectedAddons: { ...selectedAddons, [serviceId]: addonIds },
          confirmed: false,
          confirmedAppointmentReview: false,
          // Add-ons add to the combined duration -> slot grid may change; invalidate time.
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
        });
      },
      clearAddonsForService: (serviceId) => {
        const { selectedAddons } = get();
        const next = { ...selectedAddons };
        delete next[serviceId];
        // Add-ons add to the combined duration -> slot grid may change; invalidate time.
        set({
          ...pruneEntryPreferences(get(), get().selectedServiceInstances, next),
          selectedAddons: next,
          confirmed: false,
          confirmedAppointmentReview: false,
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
        });
      },

      // Category filtering
      setSelectedCategory: (category) => set({ selectedCategory: category }),
      clearCategoryFilter: () => set({ selectedCategory: "" }),

      // Provider selection
      setSelectedProviderId: (providerId, providerName) => {
        const { selectedProviderId: prevProviderId, patientType } = get();
        // Only track if value is actually changing and not during rehydration
        if (
          !isRehydrating &&
          providerId !== null &&
          providerId !== prevProviderId
        ) {
          posthog.capture("self_schedule_provider_selected", {
            provider_id: providerId,
            provider_name: providerName,
          });
          trackProviderSelection(providerId, providerName, patientType);
        }
        const providerChanged = providerId !== prevProviderId;
        set({
          selectedProviderId: providerId,
          confirmed: providerChanged ? false : get().confirmed,
          selectedDateTime: providerChanged ? null : get().selectedDateTime,
          selectedAvailabilityAssignments: providerChanged
            ? []
            : get().selectedAvailabilityAssignments,
          confirmedAppointmentReview: providerChanged
            ? false
            : get().confirmedAppointmentReview,
        });
      },

      selectAvailabilitySlot: (
        dateTime,
        assignments,
        locationId = null,
        end,
      ) => {
        const { selectedDateTime: previousDateTime, patientType } = get();
        if (
          !isRehydrating &&
          previousDateTime?.getTime() !== dateTime.getTime()
        ) {
          posthog.capture("self_schedule_time_selected", {
            time: dateTime.toISOString(),
          });
          trackTimeSelection(dateTime, patientType);
        }
        set((state) => ({
          selectedDateTime: dateTime,
          selectedAvailabilityAssignments: assignments,
          selectedSlotLocationId: locationId,
          selectedAvailabilityWindow: end
            ? { start: dateTime.toISOString(), end: end.toISOString() }
            : null,
          availabilitySelectionRevision:
            state.availabilitySelectionRevision + 1,
          confirmed: false,
        }));
      },

      setServiceInstanceProvider: (instanceId, providerId) =>
        set((state) => {
          const providerChanged =
            state.serviceInstanceProviderIds[instanceId] !== providerId ||
            Boolean(state.preferredProviderEntryKeys[instanceId]);
          return {
            serviceInstanceProviderOrigins: {
              ...state.serviceInstanceProviderOrigins,
              [instanceId]: "review",
            },
            preferredProviderEntryKeys: Object.fromEntries(
              Object.entries(state.preferredProviderEntryKeys).filter(
                ([key]) => key !== instanceId,
              ),
            ),
            serviceInstanceProviderIds: {
              ...state.serviceInstanceProviderIds,
              [instanceId]: providerId,
            },
            confirmed: false,
            selectedDateTime: providerChanged ? null : state.selectedDateTime,
            selectedAvailabilityAssignments: providerChanged
              ? []
              : state.selectedAvailabilityAssignments,
            confirmedAppointmentReview: providerChanged
              ? false
              : state.confirmedAppointmentReview,
          };
        }),

      setServiceInstancesProvider: (instanceIds, providerId) =>
        set((state) => {
          const providerChanged = instanceIds.some(
            (instanceId) =>
              state.serviceInstanceProviderIds[instanceId] !== providerId,
          );
          if (
            !providerChanged &&
            !instanceIds.some(
              (id) =>
                state.preferredProviderEntryKeys[id] ||
                state.serviceInstanceProviderOrigins[id] !== "review",
            )
          )
            return state;

          const serviceInstanceProviderIds = {
            ...state.serviceInstanceProviderIds,
          };
          for (const instanceId of instanceIds) {
            serviceInstanceProviderIds[instanceId] = providerId;
          }
          return {
            serviceInstanceProviderIds,
            serviceInstanceProviderOrigins: {
              ...state.serviceInstanceProviderOrigins,
              ...Object.fromEntries(
                instanceIds.map((id) => [id, "review" as const]),
              ),
            },
            preferredProviderEntryKeys: Object.fromEntries(
              Object.entries(state.preferredProviderEntryKeys).filter(
                ([key]) => !instanceIds.includes(key),
              ),
            ),
            confirmed: false,
            selectedDateTime: null,
            selectedAvailabilityAssignments: [],
            confirmedAppointmentReview: false,
          };
        }),

      clearReviewProviderPreferences: () =>
        set((state) => {
          const liveIds = new Set(
            state.selectedServiceInstances.map(
              (instance) => instance.instanceId,
            ),
          );
          const kept = Object.fromEntries(
            Object.entries(state.serviceInstanceProviderIds).filter(
              ([id]) =>
                liveIds.has(id) &&
                state.serviceInstanceProviderOrigins[id] === "imported",
            ),
          );
          const cleared = new Set(
            Object.keys(state.serviceInstanceProviderIds).filter(
              (id) => !(id in kept),
            ),
          );
          return {
            serviceInstanceProviderIds: kept,
            serviceInstanceProviderOrigins: Object.fromEntries(
              Object.entries(state.serviceInstanceProviderOrigins).filter(
                ([id]) => id in kept,
              ),
            ),
            // Keep references to a still-live parent. Its next materialized
            // provider remains the intended anchor; never substitute a neighbor.
            preferredProviderEntryKeys: Object.fromEntries(
              Object.entries(state.preferredProviderEntryKeys).filter(
                ([source]) => !cleared.has(source),
              ),
            ),
            confirmed: false,
            confirmedServiceSelections: false,
            confirmedAppointmentReview: false,
            selectedDateTime: null,
            selectedAvailabilityAssignments: [],
          };
        }),
      clearServiceInstanceProviders: () =>
        set({
          serviceInstanceProviderIds: {},
          serviceInstanceProviderOrigins: {},
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
          confirmedAppointmentReview: false,
        }),

      // Location selection
      setSelectedLocationId: (locationId, locationName) => {
        const { selectedLocationId: prevLocationId, patientType } = get();
        // Only track if value is actually changing and not during rehydration
        if (
          !isRehydrating &&
          locationId !== null &&
          locationId !== prevLocationId
        ) {
          posthog.capture("self_schedule_location_selected", {
            location_id: locationId,
            location_name: locationName,
          });
          trackLocationSelection(locationId, locationName, patientType);
        }
        const locationChanged = locationId !== prevLocationId;
        set({
          selectedLocationId: locationId,
          confirmed: locationChanged ? false : get().confirmed,
          selectedDateTime: locationChanged ? null : get().selectedDateTime,
          serviceInstanceProviderIds:
            locationChanged && get().providerPreferenceMode !== "per-occurrence"
              ? {}
              : get().serviceInstanceProviderIds,
          serviceInstanceProviderOrigins:
            locationChanged && get().providerPreferenceMode !== "per-occurrence"
              ? {}
              : get().serviceInstanceProviderOrigins,
          selectedAvailabilityAssignments: locationChanged
            ? []
            : get().selectedAvailabilityAssignments,
          confirmedAppointmentReview: locationChanged
            ? false
            : get().confirmedAppointmentReview,
        });
      },

      // User location
      setUserLocation: (location) => set({ userLocation: location }),
      requestUserLocation: () => {
        return new Promise<void>((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error("Geolocation is not supported by this browser."));
            return;
          }

          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
              };
              set({ userLocation });
              resolve();
            },
            (error) => {
              let errorMessage: string;
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  errorMessage =
                    "Location access was denied. Please enable location services.";
                  break;
                case error.POSITION_UNAVAILABLE:
                  errorMessage = "Location information is unavailable.";
                  break;
                case error.TIMEOUT:
                  errorMessage = "Location request timed out.";
                  break;
                default:
                  errorMessage =
                    "An unknown error occurred while getting location.";
              }
              reject(new Error(errorMessage));
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 60000,
            },
          );
        });
      },

      // Time selection
      setSelectedDateTime: (dateTime) => {
        const { selectedDateTime: prevDateTime, patientType } = get();
        // Only track if value is actually changing and not during rehydration
        if (!isRehydrating && dateTime !== null) {
          const prevTime =
            prevDateTime instanceof Date ? prevDateTime.getTime() : null;
          const newTime = dateTime.getTime();
          if (prevTime !== newTime) {
            posthog.capture("self_schedule_time_selected", {
              time: dateTime.toISOString(),
            });
            trackTimeSelection(dateTime, patientType);
          }
        }
        set({
          selectedDateTime: dateTime,
          selectedAvailabilityAssignments: dateTime
            ? get().selectedAvailabilityAssignments
            : [],
          confirmed:
            prevDateTime instanceof Date &&
            dateTime instanceof Date &&
            prevDateTime.getTime() === dateTime.getTime()
              ? get().confirmed
              : false,
        });
      },
      clearSelectedDateTime: () =>
        set({
          selectedDateTime: null,
          selectedAvailabilityAssignments: [],
          confirmed: false,
        }),

      // Utility methods
      resetAll: () =>
        set({ ...INITIAL_STATE, bookingScope: get().bookingScope }),

      setPaymentSuccessful: (paymentSuccessful) => set({ paymentSuccessful }),

      // Payment timer
      setPaymentDeadline: (deadline) => set({ paymentDeadline: deadline }),
      getPaymentTimeRemaining: () => {
        const { paymentDeadline } = get();
        if (!paymentDeadline) return 0;
        const now = new Date().getTime();
        const deadline = new Date(paymentDeadline).getTime();
        const remaining = Math.round((deadline - now) / 1000);
        return remaining > 0 ? remaining : 0;
      },

      cancelServiceId: () => {
        const { createdAppointmentId } = get();
        if (createdAppointmentId) {
          set({ createdAppointmentId: null });
        }
      },

      // Grouping preferences
      setGroupingMode: (mode) => set({ groupingMode: mode }),

      // Existing appointment
      setCancelEventId: (id) => set({ cancelEventId: id }),
      setPatientNote: (note) => set({ patientNote: note }),

      // Payment method collection
      setPaymentMethodCollected: (collected) =>
        set({ paymentMethodCollected: collected }),

      // Google Reserve with Google (RWG) tracking.
      // Persist the token + merchant id in a separate 30-day store (per
      // Google's spec) so the general self-schedule persistence — which
      // resets after 12h of inactivity — can't drop attribution for
      // patients who take longer than that to complete a booking.
      setRwgToken: (token) => {
        set({ rwgToken: token });
        if (token !== null) {
          writeRwgAttribution({ token, merchantId: get().rwgMerchantId });
        }
      },
      setRwgMerchantId: (merchantId) => {
        set({ rwgMerchantId: merchantId });
        if (merchantId !== null) {
          writeRwgAttribution({ token: get().rwgToken, merchantId });
        }
      },
    }),
    {
      name: "self-schedule-store",
      partialize: (state) => ({
        bookingScope: state.bookingScope,
        selectedServiceInstances: state.selectedServiceInstances,
        selectedAddons: state.selectedAddons,
        selectedCategory: state.selectedCategory,
        selectedProviderId: state.selectedProviderId,
        selectedAvailabilityAssignments: state.selectedAvailabilityAssignments,
        selectedAvailabilityWindow: state.selectedAvailabilityWindow,
        serviceInstanceProviderIds: state.serviceInstanceProviderIds,
        serviceInstanceProviderOrigins: state.serviceInstanceProviderOrigins,
        providerPreferenceMode: state.providerPreferenceMode,
        addonProviderIds: state.addonProviderIds,
        preferredProviderEntryKeys: state.preferredProviderEntryKeys,
        selectedLocationId: state.selectedLocationId,
        selectedDateTime: state.selectedDateTime,
        selectedSlotLocationId: state.selectedSlotLocationId,
        lastUpdated: Date.now(),
        paymentSuccessful: state.paymentSuccessful,
        locationType: state.locationType,
        serviceSearchQuery: state.serviceSearchQuery,
        groupingMode: state.groupingMode,
        currentStep: state.currentStep,
        cancelEventId: state.cancelEventId,
        dismissedExistingAppointment: state.dismissedExistingAppointment,
        appliedCarePlanStepId: state.appliedCarePlanStepId,
        bookingEventId: state.bookingEventId,
        paymentMethodCollected: state.paymentMethodCollected,
        confirmedServiceSelections: state.confirmedServiceSelections,
        confirmedAppointmentReview: state.confirmedAppointmentReview,
        // rwgToken / rwgMerchantId are intentionally NOT persisted here —
        // they live in the 30-day rwg-attribution store instead. See
        // setRwgToken / setRwgMerchantId above and lib/rwg-attribution.ts.
      }),
      // Discard older drafts whose patient identity was not recorded.
      version: 11,
      migrate: (persisted, version) => {
        if (version < 11) return { ...INITIAL_STATE, lastUpdated: Date.now() };
        const fields = persisted as Partial<SelfScheduleFields>;
        return {
          ...INITIAL_STATE,
          ...fields,
          lastUpdated: fields.lastUpdated ?? Date.now(),
        };
      },
      storage: createTenantStorage(),
      onRehydrateStorage: () => (state) => {
        isRehydrating = true;
        if (state && isSelfScheduleComplete(state.currentStep)) {
          state.resetAll();
        } else {
          state?.setCurrentStep(null, "forward");

          if (state?.selectedDateTime) {
            state.setSelectedDateTime(new Date(state.selectedDateTime));
          }
          if (state?.lastUpdated) {
            const hoursSinceLastUpdate =
              (Date.now() - state.lastUpdated) / (1000 * 60 * 60);
            if (hoursSinceLastUpdate > 12) {
              state.resetAll();
            }
          }
        }
        isRehydrating = false;
      },
    },
  ),
);

// Listen for tenant changes and load tenant-specific data
createTenantChangeListener("self-schedule-store", (tenantData) => {
  // `confirmed` is intentionally session-scoped to the exact appointment
  // details the patient reviewed. Older persisted payloads may still contain
  // it, so never restore it from tenant storage.
  // RWG attribution lives in its own tenant-scoped 30-day store — reload it
  // for the new tenant here so the in-memory token/merchantId can never
  // carry over from the previous tenant and be sent with the next booking.
  const rwg = readRwgAttribution();
  useSelfScheduleStore.setState({
    ...INITIAL_STATE,
    ...tenantData,
    confirmed: false,
    rwgToken: rwg.token,
    rwgMerchantId: rwg.merchantId,
  });
});
